// Tenant-aware JSON-LD builders. Each builder takes a SeoCtx with the active
// tenant's siteUrl + siteName so the same code produces correct schema for
// santa-teresa, nosara, barcelona, etc. Pass `{ siteUrl: tenant.siteUrl,
// siteName: tenant.siteName }` from a component that has called useTenant().
//
// Share images (og:image) resolve most-specific-first, see resolveShareImage.
// scripts/lib/shell-meta.mjs mirrors the site-level part of that precedence
// for the static SPA shell; src/test/shellMeta.test.ts keeps them in step.

import { countryName, type Tenant } from "@/lib/tenants";
import { amenityByLabel } from "@/lib/amenities";
import { foldKey } from "@/lib/foldKey";

/** Neutral 1200×630 card shipped with the build (scripts/make-social-default.mjs). */
export const DEFAULT_SHARE_IMAGE_PATH = "/social-default.jpg";
export const SHARE_CARD_WIDTH = 1200;
export const SHARE_CARD_HEIGHT = 630;

export type ShareImageSource = "page" | "pageCard" | "siteCard" | "tenant" | "default";

export interface ShareImage {
  url: string;
  width?: number;
  height?: number;
  /** MIME type when known. */
  type?: string;
  alt: string;
  source: ShareImageSource;
}

export interface ShareImageInputs {
  /** The page's own image (listing photo, area hero), absolute or site-relative. */
  explicit?: string | null;
  explicitAlt?: string | null;
  explicitDims?: { width: number; height: number; type?: string } | null;
  /** The admin's card for this page (og.page.<id>), already a 1200×630 URL. */
  pageCard?: string | null;
  pageCardAlt?: string | null;
  /** The admin's site card (seo.ogImage), already a 1200×630 URL. */
  uploaded?: string | null;
  uploadedAlt?: string | null;
  /** Tenants → social card URL (size unknown). */
  tenantUrl?: string | null;
  siteUrl: string;
  siteName?: string | null;
}

const MIME_BY_EXT: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  avif: "image/avif",
};

/** MIME type from a URL's file extension (query string ignored), else undefined. */
export const mimeFromUrl = (url: string): string | undefined => {
  const path = url.split(/[?#]/)[0];
  const ext = /\.([a-z0-9]+)$/i.exec(path)?.[1]?.toLowerCase();
  return ext ? MIME_BY_EXT[ext] : undefined;
};

/**
 * og:image precedence: the page's own image (listing photo, area hero) → the
 * page's admin card → the site card → the tenant's fallback URL (Tenants →
 * social card URL) → the neutral default card shipped with the build.
 * Site-relative URLs are made absolute against the tenant's site URL, as
 * scrapers require. Cards are served at 1200×630, so their size is stated;
 * a tenant URL's size is unknown and omitted.
 */
export const resolveShareImage = (o: ShareImageInputs): ShareImage => {
  const base = o.siteUrl.replace(/\/$/, "");
  const abs = (u: string) => (u.startsWith("/") ? `${base}${u}` : u);
  const siteAlt = o.siteName ? `${o.siteName}, monthly & seasonal rentals` : "Monthly & seasonal rentals";
  const card = (url: string) => ({ width: SHARE_CARD_WIDTH, height: SHARE_CARD_HEIGHT, type: mimeFromUrl(url) });
  const explicit = o.explicit?.trim();
  if (explicit) {
    return { url: abs(explicit), alt: o.explicitAlt?.trim() || siteAlt, source: "page", ...(o.explicitDims ?? {}) };
  }
  const pageCard = o.pageCard?.trim();
  if (pageCard) return { url: pageCard, alt: o.pageCardAlt?.trim() || siteAlt, source: "pageCard", ...card(pageCard) };
  const uploaded = o.uploaded?.trim();
  if (uploaded) return { url: uploaded, alt: o.uploadedAlt?.trim() || siteAlt, source: "siteCard", ...card(uploaded) };
  const tenantUrl = o.tenantUrl?.trim();
  if (tenantUrl) return { url: abs(tenantUrl), alt: siteAlt, source: "tenant", type: mimeFromUrl(tenantUrl) };
  const url = `${base}${DEFAULT_SHARE_IMAGE_PATH}`;
  return { url, alt: siteAlt, source: "default", ...card(url) };
};

/** The share image URL alone (see resolveShareImage). */
export const pickOgImage = (o: ShareImageInputs): string => resolveShareImage(o).url;

export interface SeoCtx {
  siteUrl: string;
  siteName: string;
}

const join = (siteUrl: string, path: string): string => {
  if (path.startsWith("http")) return path;
  const slash = path.startsWith("/") ? "" : "/";
  return `${siteUrl}${slash}${path}`;
};

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export const breadcrumbList = (ctx: SeoCtx, items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: join(ctx.siteUrl, it.path),
  })),
});

export const faqPage = (qa: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const placeSchema = (
  ctx: SeoCtx,
  opts: {
    name: string;
    description: string;
    url: string;
    latitude?: number;
    longitude?: number;
    image?: string;
    containedIn?: string;
  },
) => ({
  "@context": "https://schema.org",
  "@type": "Place",
  name: opts.name,
  description: opts.description,
  url: join(ctx.siteUrl, opts.url),
  ...(opts.image ? { image: opts.image } : {}),
  ...(opts.latitude != null && opts.longitude != null
    ? {
        geo: {
          "@type": "GeoCoordinates",
          latitude: opts.latitude,
          longitude: opts.longitude,
        },
      }
    : {}),
  ...(opts.containedIn
    ? {
        containedInPlace: {
          "@type": "Place",
          name: opts.containedIn,
        },
      }
    : {}),
});

interface ListingOpts {
  name: string;
  description: string;
  url: string;
  images: string[];
  /** apartment | condo | house | studio | villa, picks the schema.org subtype. */
  type?: string | null;
  bedrooms?: number;
  bathrooms?: number;
  maxGuests?: number;
  size?: number | null;
  petsAllowed?: boolean;
  /** Declared amenity labels (registry labels; legacy free text allowed). */
  amenities?: string[];
  /** Essentials the owner explicitly said are NOT there. */
  amenitiesAbsent?: string[];
  /** Services included in the rent, as labels ("Cleaning", "Electricity"…). */
  servicesIncluded?: string[];
  /** Minimum stay in months → leaseLength. */
  minRentalMonths?: number | null;
  /** yyyy-MM-dd the home becomes available → offers.availabilityStarts. */
  availableFrom?: string | null;
  area: string;
  region?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  pricePerMonth: number;
  priceMax?: number | null;
  currency?: string;
  available?: boolean;
}

/** schema.org subtype for a listing type; plain Accommodation when unknown. */
export const accommodationType = (type?: string | null): "Apartment" | "House" | "Accommodation" => {
  const t = (type ?? "").toLowerCase();
  if (t === "apartment" || t === "condo" || t === "studio") return "Apartment";
  if (t === "house" || t === "villa") return "House";
  return "Accommodation";
};

/**
 * amenityFeature entries: what the home declares (value true), what the owner
 * ruled out (value false), and the services the rent includes. propertyID
 * carries the registry key (or the schema.org/Google token when one exists)
 * so agents get a stable identifier next to the human label. Pet types stay
 * out (petsAllowed covers pets).
 */
export const amenityFeatures = (opts: Pick<ListingOpts, "amenities" | "amenitiesAbsent" | "servicesIncluded">) => {
  const feature = (label: string, value: boolean) => {
    const spec = amenityByLabel(label);
    return { "@type": "LocationFeatureSpecification", name: label, value, propertyID: spec?.schemaName ?? spec?.key ?? foldKey(label) };
  };
  const present = (opts.amenities ?? []).filter((l) => l && !amenityByLabel(l)?.petType);
  const absent = (opts.amenitiesAbsent ?? []).filter((l) => l && !present.includes(l));
  return [
    ...present.map((l) => feature(l, true)),
    ...absent.map((l) => feature(l, false)),
    ...(opts.servicesIncluded ?? []).map((s) => ({
      "@type": "LocationFeatureSpecification",
      name: `${s} included`,
      value: true,
      propertyID: `service:${foldKey(s)}`,
    })),
  ];
};

export const accommodationSchema = (ctx: SeoCtx, opts: ListingOpts) => ({
  "@context": "https://schema.org",
  "@type": accommodationType(opts.type),
  name: opts.name,
  description: opts.description,
  url: join(ctx.siteUrl, opts.url),
  image: opts.images,
  ...(opts.type ? { accommodationCategory: opts.type.charAt(0).toUpperCase() + opts.type.slice(1) } : {}),
  ...(opts.bedrooms != null ? { numberOfBedrooms: opts.bedrooms } : {}),
  ...(opts.bathrooms != null
    ? { numberOfBathroomsTotal: opts.bathrooms }
    : {}),
  ...(opts.maxGuests != null ? { occupancy: { "@type": "QuantitativeValue", maxValue: opts.maxGuests } } : {}),
  ...(opts.size
    ? {
        floorSize: {
          "@type": "QuantitativeValue",
          value: opts.size,
          unitCode: "MTK",
        },
      }
    : {}),
  ...(opts.petsAllowed != null ? { petsAllowed: opts.petsAllowed } : {}),
  ...(opts.minRentalMonths && opts.minRentalMonths > 0 ? { leaseLength: `P${Math.round(opts.minRentalMonths)}M` } : {}),
  ...(() => {
    const features = amenityFeatures(opts);
    return features.length ? { amenityFeature: features } : {};
  })(),
  address: {
    "@type": "PostalAddress",
    addressLocality: opts.area,
    ...(opts.region ? { addressRegion: opts.region } : {}),
    ...(opts.country ? { addressCountry: opts.country } : {}),
  },
  ...(opts.latitude != null && opts.longitude != null
    ? {
        geo: {
          "@type": "GeoCoordinates",
          latitude: opts.latitude,
          longitude: opts.longitude,
        },
      }
    : {}),
  offers: {
    "@type": "Offer",
    price: opts.pricePerMonth,
    priceCurrency: opts.currency || "USD",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: opts.pricePerMonth,
      priceCurrency: opts.currency || "USD",
      unitText: "MONTH",
      ...(opts.priceMax && opts.priceMax > opts.pricePerMonth
        ? { maxPrice: opts.priceMax }
        : {}),
    },
    availability:
      opts.available === false
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
    ...(opts.availableFrom ? { availabilityStarts: opts.availableFrom } : {}),
    url: join(ctx.siteUrl, opts.url),
  },
});

export const itemListSchema = (
  ctx: SeoCtx,
  name: string,
  items: { name: string; url: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: join(ctx.siteUrl, it.url),
  })),
});

/**
 * A collection landing page: the page itself plus the homes it lists as its
 * main entity, so crawlers read it as a curated list rather than a search.
 */
export const collectionPageSchema = (
  ctx: SeoCtx,
  opts: { name: string; description: string; path: string; items: { name: string; url: string }[] },
) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: opts.name,
  description: opts.description,
  url: join(ctx.siteUrl, opts.path),
  isPartOf: { "@type": "WebSite", name: ctx.siteName, url: ctx.siteUrl },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: opts.items.length,
    itemListElement: opts.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: join(ctx.siteUrl, it.url),
    })),
  },
});

export const contactPageSchema = (ctx: SeoCtx, url: string) => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: join(ctx.siteUrl, url),
  name: `Contact ${ctx.siteName}`,
  publisher: {
    "@type": "Organization",
    name: ctx.siteName,
    url: ctx.siteUrl,
  },
});

// ---------------------------------------------------------------------------
// Site-wide identity schema (Organization / WebSite / RealEstateAgent / FAQ).
// Rendered once on the home page from the active tenant, replaces the static,
// Santa-Teresa-hardcoded JSON-LD that used to live in index.html (the
// Cloudflare Worker rewrites <title>/og: but can't edit inside ld+json).
// ---------------------------------------------------------------------------

const siteUrlNoSlash = (tenant: Tenant): string => tenant.siteUrl.replace(/\/$/, "");

const socialLinks = (tenant: Tenant): string[] =>
  [tenant.instagramUrl, tenant.facebookUrl].filter((u): u is string => !!u);

export const organizationSchema = (tenant: Tenant, opts: { logo?: string | null } = {}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: tenant.siteName,
  url: tenant.siteUrl,
  // The uploaded logo (Site assets → Brand) when there is one, else the
  // favicon the build ships.
  logo: opts.logo?.trim() || `${siteUrlNoSlash(tenant)}/favicon.ico`,
  description: `Mid-to-long-term gated rental marketplace for ${tenant.cityLabel} and ${tenant.regionLabel}, ${countryName(tenant.country)}.`,
  ...(tenant.contactEmail ? { email: tenant.contactEmail } : {}),
  areaServed: {
    "@type": "Place",
    name: `${tenant.cityLabel}, ${tenant.regionLabel}, ${countryName(tenant.country)}`,
  },
  ...(tenant.contactEmail
    ? {
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: tenant.contactEmail,
          areaServed: tenant.country,
          availableLanguage: ["English", "Spanish"],
        },
      }
    : {}),
  ...(socialLinks(tenant).length ? { sameAs: socialLinks(tenant) } : {}),
});

export const webSiteSchema = (tenant: Tenant) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: tenant.siteName,
  url: tenant.siteUrl,
  description: `Monthly and seasonal rentals in ${tenant.cityLabel} and ${tenant.regionLabel}, ${countryName(tenant.country)}.`,
  // No SearchAction: the browse page has filters (see src/lib/browseFilters.ts),
  // not a keyword box, and a sitelinks searchbox pointing at a param the page
  // ignores would send people to an unfiltered list.
});

export const realEstateAgentSchema = (tenant: Tenant, opts: { image?: string | null } = {}) => ({
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: tenant.siteName,
  url: tenant.siteUrl,
  description: `Marketplace for monthly and seasonal home rentals in ${tenant.cityLabel} and ${tenant.regionLabel}, ${countryName(tenant.country)}.`,
  // The same card the share tags use, so the schema and og:image agree.
  image:
    opts.image?.trim() ||
    resolveShareImage({ tenantUrl: tenant.ogImage, siteUrl: tenant.siteUrl, siteName: tenant.siteName }).url,
  priceRange: "$$-$$$$",
  address: {
    "@type": "PostalAddress",
    ...(tenant.streetAddress ? { streetAddress: tenant.streetAddress } : {}),
    addressLocality: tenant.cityLabel,
    addressRegion: tenant.addressRegion,
    ...(tenant.postalCode ? { postalCode: tenant.postalCode } : {}),
    addressCountry: tenant.country,
  },
  ...(tenant.geoLat != null && tenant.geoLng != null
    ? { geo: { "@type": "GeoCoordinates", latitude: tenant.geoLat, longitude: tenant.geoLng } }
    : {}),
  areaServed: tenant.areas.map((a) => ({
    "@type": "Place",
    name: `${a}, ${countryName(tenant.country)}`,
  })),
});

export const siteFaqSchema = (tenant: Tenant) => {
  const country = countryName(tenant.country);
  return faqPage([
    {
      q: `What is ${tenant.siteName}?`,
      a: `${tenant.siteName} is a gated rental marketplace focused on monthly and seasonal stays in ${tenant.cityLabel} and ${tenant.regionLabel}, ${country}. It replaces scattered WhatsApp chats and Facebook groups with structured listings that include real prices, amenities, photos, and live availability calendars.`,
    },
    {
      q: `Which areas does ${tenant.siteName} cover?`,
      a: `The platform covers ${tenant.areas.length} areas in and around ${tenant.cityLabel}: ${tenant.areas.join(", ")}.`,
    },
    {
      q: "How long are typical rentals?",
      a: "Listings are designed for mid-to-long-term stays, typically one month or longer, including seasonal rentals across the year.",
    },
    {
      q: `How do I list my home on ${tenant.siteName}?`,
      a: "Property owners can sign up and complete a short onboarding flow at /owner-onboarding. Listings require real photos, key amenities, and a complete availability calendar.",
    },
    {
      q: "Is browsing free for renters?",
      a: "Yes. Browsing the marketplace is free. Renters create a free account, complete a profile with their dates and preferences, and the platform shows a match score for each property. Owner contact details are revealed only after a contact request is accepted.",
    },
    {
      q: `Does ${tenant.siteName} handle bookings or payments?`,
      a: `No. The platform connects renters with owners directly. Once a contact request is accepted, both parties communicate and arrange the booking and payment between themselves, the same way the local rental market already works in ${tenant.cityLabel}.`,
    },
  ]);
};
